import React, {useCallback, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import '../PartnerReferalRewardModal/PartnerReferalRewardModal.css';
import '../LoginRequiredModal/LoginRequiredModal.css';
import '../../YandexAuthPage/EmailAuthForm.css';
import PageButton from '../../elements/PageButton/PageButton';
import {API_BASE} from '../../../utils/apiConfig';
import {sendPageEvent} from '../../../analytics/gameAnalytics';

const PAGE_ID = 'PasswordChangeModal';

/**
 * Смена пароля из настроек.
 *
 * Сам пароль здесь не вводится: мы отправляем письмо со ссылкой, и новый
 * пароль человек задает уже по ней (PasswordResetPage). Так смена пароля
 * требует доступа к почте — иначе любой, кто добрался до чужого открытого
 * браузера, менял бы пароль и забирал аккаунт себе.
 *
 * Оформление и портал в body — как у модалки входа (см. LoginRequiredModal:
 * иначе модалку запирает контекст наложения родителя).
 */
function PasswordChangeModal({onClose}) {
  const [visible, setVisible] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [sentToEmail, setSentToEmail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    sendPageEvent(PAGE_ID, 'mount');
    const showTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(showTimer);
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => onClose?.(), 300);
  }, [onClose]);

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const requestChange = useCallback(async () => {
    setError(null);
    setIsBusy(true);
    try {
      const response = await fetch(`${API_BASE}/auth/password/change-request`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data.error || 'Не удалось отправить письмо');
        return;
      }
      setSentToEmail(data.email || '');
    } catch (requestError) {
      console.error('Password change request error', requestError);
      setError('Нет связи с сервером');
    } finally {
      setIsBusy(false);
    }
  }, []);

  return createPortal(
    <div
      className={`partner-referal-reward-overlay login-required-modal-overlay ${visible ? 'partner-referal-reward-overlay_visible' : ''}`}
      onClick={handleOverlayClick}
      role={'dialog'}
      aria-modal={'true'}
    >
      <div className={`partner-referal-reward-modal ${visible ? 'partner-referal-reward-modal_visible' : ''}`}>
        <h2 className={'partner-referal-reward-title login-required-modal-title'}>
          {sentToEmail ? 'Письмо отправлено' : 'Смена пароля'}
        </h2>

        <div className={'main-menu-buttons partner-referal-reward-buttons'}>
          {sentToEmail ? (
            <>
              <p className={'email-auth-text'}>
                Мы отправили ссылку на <b>{sentToEmail}</b>.
                <br/>Открой ее и задай новый пароль.
              </p>
              <p className={'email-auth-hint'}>
                Ссылка действует час. Проверь и папку «Спам».
              </p>
            </>
          ) : (
            <>
              <p className={'email-auth-text'}>
                Мы отправим на твою почту ссылку — по ней ты задашь новый пароль.
              </p>
              {error && <p className={'email-auth-error'}>{error}</p>}
              <PageButton
                onClick={requestChange}
                disabled={isBusy}
                pageId={PAGE_ID}
                buttonId={'request'}
              >
                {isBusy ? 'Отправляем...' : 'Отправить письмо'}
              </PageButton>
            </>
          )}

          <PageButton
            className={'main-menu-button_secondary'}
            onClick={handleClose}
            pageId={PAGE_ID}
            buttonId={'close'}
          >
            {sentToEmail ? 'Готово' : 'Отмена'}
          </PageButton>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default PasswordChangeModal;
